import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Paper,
  Collapse,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function ProductDetails() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Get user role for permissions
  const userRole = localStorage.getItem("role");
  const isStaff = userRole === "Staff";
  
  // Get tab from URL params to preserve navigation context
  const searchParams = new URLSearchParams(window.location.search);
  const tabFromUrl = searchParams.get('tab') || '0';

  useEffect(() => {
    fetchProductDetails();
  }, [code]);

  const fetchProductDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/inventory/products/${code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setProduct(data.product);
        setPurchaseHistory(data.product.purchaseHistory || []);
      } else {
        toast.error("Product not found");
        navigate("/inventory");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product details");
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ 
          p: 3, 
          textAlign: "center",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Typography sx={{ color: "#C59A58", fontSize: "1.2rem" }}>
            Loading...
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  if (!product) {
    return (
      <AppLayout>
        <Box sx={{ 
          p: 3, 
          textAlign: "center",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Typography sx={{ color: "#C59A58", fontSize: "1.2rem" }}>
            Product not found
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ 
        maxWidth: "1400px", 
        mx: "auto", 
        p: { xs: 2, sm: 3 },
        minHeight: "100vh"
      }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/inventory?tab=${tabFromUrl}`)}
          sx={{ 
            mb: 3,
            color: "#C59A58",
            borderColor: "#C59A58",
            textTransform: 'none',
            fontSize: { xs: "0.9rem", sm: "1rem" },
            "&:hover": {
              bgcolor: "rgba(197, 154, 88, 0.1)",
              borderColor: "#C59A58"
            }
          }}
          variant="outlined"
        >
          Back to Inventory
        </Button>

        {/* Page Title */}
        <Typography variant="h4" sx={{ 
          fontWeight: 700, 
          mb: 4,
          color: "#C59A58",
          fontSize: { xs: "1.75rem", sm: "2.125rem" }
        }}>
          Product Details
        </Typography>

        {/* Main Layout Container */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3
        }}>
          {/* Left Column - Product Image (35% on md+, 100% on mobile) */}
          <Box sx={{ flex: { xs: '0 0 100%', md: '0 0 35%' } }}>
            <Card sx={{
              bgcolor: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)"
            }}>
              <CardContent sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: { xs: 250, sm: 300, md: 350 }, 
                p: { xs: 1.5, sm: 2 } 
              }}>
                {product.Photo ? (
                  <Box
                    component="img"
                    src={`${API_BASE_URL}${product.Photo}`}
                    alt={product.ProductName}
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: 2,
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: { xs: 200, sm: 250, md: 300 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#2a2a2a",
                      borderRadius: 2,
                      border: "2px dashed #444"
                    }}
                  >
                    <Typography color="#888" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                      No Image Available
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Right Column - Product Details & Size Variants (65% on md+, 100% on mobile) */}
          <Box sx={{ flex: { xs: '0 0 100%', md: '0 0 65%' }, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Product Details Card */}
            <Card sx={{
              bgcolor: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)"
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="h5" sx={{ 
                  mb: 2, 
                  color: '#fff', 
                  fontWeight: 'bold', 
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.3rem' } 
                }}>
                  {product.ProductName}
                </Typography>

                {/* Compact horizontal details layout */}
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
                  gap: { xs: 1.5, sm: 2, md: 2 },
                  mb: 2
                }}>
                  {/* Product Code */}
                  <Box>
                    <Typography sx={{ 
                      color: '#888', 
                      fontSize: '0.75rem', 
                      mb: 0.4, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px' 
                    }}>
                      Product Code
                    </Typography>
                    <Typography sx={{ 
                      color: '#fff', 
                      fontWeight: '500', 
                      fontSize: { xs: '0.9rem', sm: '0.95rem' } 
                    }}>
                      {product.ProductCode}
                    </Typography>
                  </Box>

                  {/* Category */}
                  <Box>
                    <Typography sx={{ 
                      color: '#888', 
                      fontSize: '0.75rem', 
                      mb: 0.4, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px' 
                    }}>
                      Category
                    </Typography>
                    <Chip 
                      label={product.Category} 
                      size="small" 
                      sx={{ 
                        bgcolor: '#C59A58', 
                        color: '#000', 
                        fontWeight: 'bold', 
                        fontSize: '0.7rem' 
                      }} 
                    />
                  </Box>

                  {/* Product Type */}
                  <Box>
                    <Typography sx={{ 
                      color: '#888', 
                      fontSize: '0.75rem', 
                      mb: 0.4, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px' 
                    }}>
                      Product Type
                    </Typography>
                    <Typography sx={{ 
                      color: '#fff', 
                      fontWeight: '500', 
                      fontSize: { xs: '0.9rem', sm: '0.95rem' } 
                    }}>
                      {product.ProductType || 'N/A'}
                    </Typography>
                  </Box>

                  {/* Color */}
                  <Box>
                    <Typography sx={{ 
                      color: '#888', 
                      fontSize: '0.75rem', 
                      mb: 0.4, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px' 
                    }}>
                      Color
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: product.Color?.toLowerCase() || '#ccc',
                        }}
                      />
                      <Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>
                        {product.Color || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Selling Price */}
                  <Box>
                    <Typography sx={{ 
                      color: '#888', 
                      fontSize: '0.75rem', 
                      mb: 0.4, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px' 
                    }}>
                      Selling Price
                    </Typography>
                    <Typography sx={{ 
                      color: '#C59A58', 
                      fontWeight: 'bold', 
                      fontSize: { xs: '1rem', sm: '1.05rem' } 
                    }}>
                      ₹{product.Price}
                    </Typography>
                  </Box>

                  {/* Total Stock */}
                  <Box>
                    <Typography sx={{ 
                      color: '#888', 
                      fontSize: '0.75rem', 
                      mb: 0.4, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px' 
                    }}>
                      Total Stock
                    </Typography>
                    <Typography sx={{ 
                      color: '#fff', 
                      fontWeight: 'bold', 
                      fontSize: { xs: '1rem', sm: '1.05rem' } 
                    }}>
                      {product.sizes && product.sizes.length > 0
                        ? product.sizes.reduce((total, size) => total + (size.QuantityInStock || 0), 0)
                        : product.QuantityInStock || 0} units
                    </Typography>
                  </Box>
                </Box>

                {/* Description - Full width */}
                {product.Description && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #444' }}>
                    <Typography sx={{ 
                      color: '#888', 
                      fontSize: '0.75rem', 
                      mb: 0.5, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px' 
                    }}>
                      Description
                    </Typography>
                    <Typography sx={{ 
                      color: '#ccc', 
                      fontSize: { xs: '0.9rem', md: '0.95rem' }, 
                      lineHeight: 1.5 
                    }}>
                      {product.Description}
                    </Typography>
                  </Box>
                )}

                {/* Additional Info - Purchase Price (Admin only) */}
                {!isStaff && product.Category === "Ready Made" && product.PurchasePrice && (
                  <Box sx={{ 
                    mt: 2, 
                    pt: 2, 
                    borderTop: '1px solid #444', 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr' }, 
                    gap: 2 
                  }}>
                    <Box>
                      <Typography sx={{ 
                        color: '#888', 
                        fontSize: '0.75rem', 
                        mb: 0.4, 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.5px' 
                      }}>
                        Purchase Price
                      </Typography>
                      <Typography sx={{ color: '#fff', fontWeight: '500', fontSize: '0.95rem' }}>
                        ₹{product.PurchasePrice}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ 
                        color: '#888', 
                        fontSize: '0.75rem', 
                        mb: 0.4, 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.5px' 
                      }}>
                        Profit Margin
                      </Typography>
                      <Typography sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '0.95rem' }}>
                        ₹{(parseFloat(product.Price) - parseFloat(product.PurchasePrice)).toFixed(2)}
                        ({(((parseFloat(product.Price) - parseFloat(product.PurchasePrice)) / parseFloat(product.PurchasePrice)) * 100).toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Supplier and Purchase From */}
                {(product.SupplierName || product.PurchaseFrom) && (
                  <Box sx={{ 
                    mt: 2, 
                    pt: 2, 
                    borderTop: '1px solid #444', 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                    gap: 2 
                  }}>
                    {product.SupplierName && (
                      <Box>
                        <Typography sx={{ 
                          color: '#888', 
                          fontSize: '0.75rem', 
                          mb: 0.4, 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px' 
                        }}>
                          Supplier
                        </Typography>
                        <Typography sx={{ 
                          color: '#fff', 
                          fontWeight: '500', 
                          fontSize: { xs: '0.9rem', sm: '0.95rem' } 
                        }}>
                          {product.SupplierName}
                        </Typography>
                      </Box>
                    )}
                    {product.PurchaseFrom && (
                      <Box>
                        <Typography sx={{ 
                          color: '#888', 
                          fontSize: '0.75rem', 
                          mb: 0.4, 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px' 
                        }}>
                          Purchased From
                        </Typography>
                        <Typography sx={{ 
                          color: '#fff', 
                          fontSize: { xs: '0.9rem', sm: '0.95rem' } 
                        }}>
                          {product.PurchaseFrom}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {!isStaff && product.Category === "Ready Made" && product.PurchaseDate && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #444' }}>
                    <Typography sx={{ 
                      color: '#888', 
                      fontSize: '0.75rem', 
                      mb: 0.4, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px' 
                    }}>
                      Purchase Date
                    </Typography>
                    <Typography sx={{ 
                      color: '#fff', 
                      fontWeight: '500', 
                      fontSize: { xs: '0.9rem', sm: '0.95rem' } 
                    }}>
                      {new Date(product.PurchaseDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Size Variants & Materials Section */}
            {product.sizes && product.sizes.length > 0 && (
              <Card sx={{
                bgcolor: "#1a1a1a",
                border: "1px solid #333",
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)"
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ 
                    mb: 3, 
                    color: '#C59A58', 
                    fontWeight: 'bold', 
                    fontSize: { xs: '1rem', sm: '1.1rem' } 
                  }}>
                    Size Variants & Materials
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {product.sizes.map((size, idx) => (
                      <Box key={idx}>
                        <Box
                          onClick={() => setExpandedRows(prev => ({
                            ...prev,
                            [`size-${idx}`]: !prev[`size-${idx}`]
                          }))}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            bgcolor: '#2a2a2a',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#333' },
                            border: '1px solid #444',
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                              label={`Size: ${size.Size}`}
                              sx={{ 
                                bgcolor: '#C59A58', 
                                color: '#000', 
                                fontWeight: 'bold', 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                              }}
                            />
                            <Chip
                              label={`Stock: ${size.QuantityInStock}`}
                              sx={{ 
                                bgcolor: '#4caf50', 
                                color: '#000', 
                                fontWeight: 'bold', 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                              }}
                            />
                          </Box>
                          <IconButton size="small">
                            <ExpandMoreIcon
                              sx={{
                                color: '#C59A58',
                                transform: expandedRows[`size-${idx}`] ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s',
                              }}
                            />
                          </IconButton>
                        </Box>

                        <Collapse in={expandedRows[`size-${idx}`]} timeout="auto" unmountOnExit>
                          {product.Category === "Customized" && size.materials && size.materials.length > 0 ? (
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: '#2a2a2a', 
                              mt: 1, 
                              borderRadius: 1, 
                              border: '1px solid #444' 
                            }}>
                              <Typography sx={{ 
                                color: '#C59A58', 
                                fontWeight: 'bold', 
                                mb: 2, 
                                fontSize: { xs: '0.875rem', sm: '0.925rem' } 
                              }}>
                                Raw Materials Required:
                              </Typography>
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ 
                                        color: '#C59A58', 
                                        fontWeight: 'bold', 
                                        border: 'none', 
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                                      }}>
                                        Material Name
                                      </TableCell>
                                      <TableCell align="right" sx={{ 
                                        color: '#C59A58', 
                                        fontWeight: 'bold', 
                                        border: 'none', 
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                                      }}>
                                        Quantity
                                      </TableCell>
                                      <TableCell sx={{ 
                                        color: '#C59A58', 
                                        fontWeight: 'bold', 
                                        border: 'none', 
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                                      }}>
                                        Unit
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {size.materials.map((material, matIndex) => (
                                      <TableRow key={matIndex} sx={{ borderBottom: '1px solid #333' }}>
                                        <TableCell sx={{ 
                                          color: '#ccc', 
                                          border: 'none', 
                                          fontSize: { xs: '0.7rem', sm: '0.8rem' } 
                                        }}>
                                          {material.MaterialName}
                                        </TableCell>
                                        <TableCell align="right" sx={{ 
                                          color: '#ccc', 
                                          border: 'none', 
                                          fontSize: { xs: '0.7rem', sm: '0.8rem' } 
                                        }}>
                                          {material.QuantityRequired}
                                        </TableCell>
                                        <TableCell sx={{ 
                                          color: '#ccc', 
                                          border: 'none', 
                                          fontSize: { xs: '0.7rem', sm: '0.8rem' } 
                                        }}>
                                          {material.Unit}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          ) : (
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: '#2a2a2a', 
                              mt: 1, 
                              borderRadius: 1, 
                              border: '1px solid #444' 
                            }}>
                              <Typography sx={{ 
                                color: '#888', 
                                fontSize: { xs: '0.8rem', sm: '0.9rem' } 
                              }}>
                                No materials specified
                              </Typography>
                            </Box>
                          )}
                        </Collapse>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>

        {/* PURCHASE HISTORY - Admin only and Ready Made only */}
        {!isStaff && product.Category === "Ready Made" && (
          <Card sx={{ 
            bgcolor: '#1a1a1a', 
            border: '1px solid #333', 
            mt: 4, 
            mb: 4, 
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)"
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ 
                color: '#C59A58', 
                fontWeight: 'bold', 
                mb: 3, 
                fontSize: { xs: '1rem', sm: '1.1rem' } 
              }}>
                Purchase History
              </Typography>

              {/* Mobile Card View (< 600px) */}
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                {purchaseHistory.length > 0 ? (
                  purchaseHistory.map((purchase, index) => (
                    <Card key={purchase.PurchaseId} sx={{ 
                      mb: 2, 
                      bgcolor: "#2a2a2a", 
                      border: "1px solid #444",
                      borderRadius: 2
                    }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                          <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "#C59A58",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#000",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            mr: 2
                          }}>
                            {new Date(purchase.PurchaseDate).getDate()}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ color: "#C59A58", fontWeight: 600, fontSize: "0.9rem" }}>
                              Purchase #{index + 1}
                            </Typography>
                            <Typography sx={{ color: "#fff", fontSize: "0.8rem" }}>
                              {purchase.SupplierName || "No Supplier"}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography sx={{ color: "#888", fontSize: "0.75rem", mb: 1 }}>
                          {new Date(purchase.PurchaseDate).toLocaleDateString()}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Box>
                            <Typography sx={{ color: "#fff", fontSize: "0.75rem" }}>
                              Size: {purchase.Size || "-"} • Qty: {purchase.QuantityPurchased}
                            </Typography>
                            <Typography sx={{ color: "#C59A58", fontSize: "0.85rem", fontWeight: 600 }}>
                              ₹{parseFloat(purchase.TotalCost).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card sx={{ bgcolor: "#2a2a2a", border: "1px solid #444" }}>
                    <CardContent sx={{ textAlign: "center", py: 4 }}>
                      <Typography sx={{ color: "#888", fontSize: "0.9rem" }}>
                        No purchase history available
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Box>

              {/* Desktop Table View (≥ 600px) */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <TableContainer component={Paper} sx={{ bgcolor: '#2a2a2a', width: "100%" }}>
                  <Table sx={{ width: "100%" }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#C59A58' }}>
                        <TableCell sx={{ color: '#000', fontWeight: 'bold', fontSize: '0.875rem' }}>Date</TableCell>
                        <TableCell sx={{ color: '#000', fontWeight: 'bold', fontSize: '0.875rem' }}>Supplier</TableCell>
                        <TableCell sx={{ color: '#000', fontWeight: 'bold', fontSize: '0.875rem' }}>Size</TableCell>
                        <TableCell sx={{ color: '#000', fontWeight: 'bold', fontSize: '0.875rem' }}>Quantity</TableCell>
                        <TableCell sx={{ color: '#000', fontWeight: 'bold', fontSize: '0.875rem' }}>Total Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseHistory.length > 0 ? (
                        purchaseHistory.map((purchase, index) => (
                          <TableRow key={purchase.PurchaseId} sx={{ 
                            borderBottom: '1px solid #444',
                            "&:nth-of-type(odd)": { bgcolor: "#333" },
                            "&:nth-of-type(even)": { bgcolor: "#2a2a2a" }
                          }}>
                            <TableCell sx={{ color: '#C59A58', fontSize: '0.8rem', fontWeight: 600 }}>
                              {new Date(purchase.PurchaseDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell sx={{ color: '#fff', fontSize: '0.8rem' }}>
                              {purchase.SupplierName || '-'}
                            </TableCell>
                            <TableCell sx={{ color: '#fff', fontSize: '0.8rem' }}>
                              {purchase.Size || '-'}
                            </TableCell>
                            <TableCell sx={{ color: '#fff', fontSize: '0.8rem' }}>
                              {purchase.QuantityPurchased}
                            </TableCell>
                            <TableCell sx={{ color: '#C59A58', fontWeight: 'bold', fontSize: '0.8rem' }}>
                              ₹{parseFloat(purchase.TotalCost).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ color: '#888', textAlign: 'center', py: 3, fontSize: '0.9rem' }}>
                            No purchase history available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </CardContent>
          </Card>
        )}


      </Box>
    </AppLayout>
  );
}
